import { useMutation, useQuery, useQueryClient } from 'react-query';
import dayjs from 'dayjs';

import Auth from 'src/services/api/auth/Auth';
import playbooksAPI, {
    type IPlaybookRunResponse,
    type IEligibleRowsResponse,
} from 'src/api/playbooks-api';
import { type IStandardResponse } from 'src/common/api-utils';
import { useNotifications } from 'src/notifications';
import { useCreateActivity } from 'src/services/api/activities';
import { queryKeys } from 'src/services/api/constants';
import { usePlaybooksV2 } from 'src/services/api/playbooks/use-playbooks';

export const ELIGIBLE_ROWS_QUERY_KEY = 'eligible-rows';

export const useDeletePlaybook = (uuid?: string) => {
    const accessToken = Auth.accessToken;
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation(
        // @ts-expect-error TODO: refactor data fetching
        async () => playbooksAPI.delete(accessToken, uuid), {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.playbooks);
                queryClient.invalidateQueries(queryKeys.playbooksV2);
                queryClient.invalidateQueries(queryKeys.teamPlaybooks);
                addNotification('Playbook deleted', 'success');
            }
        },
    );
};

interface IRunPlaybookPayload {
    playbookUUID: string
    leadUUIDs: string[]
}

export const useRunPlaybook = () => {
    const accessToken = Auth.accessToken;
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();
    const { mutateAsync: createActivity } = useCreateActivity();

    const { data: playbooks } = usePlaybooksV2();

    return useMutation<IPlaybookRunResponse, unknown, IRunPlaybookPayload>(
        // @ts-expect-error TODO: refactor data fetching
        async (payload) => playbooksAPI.trigger(accessToken, payload.playbookUUID, payload.leadUUIDs), {
            onMutate: () => {
                addNotification('Playbook run confirmed. In progress...', 'info');
            },
            onSuccess: async (data) => {
                const success = data.status === 'success';
                const message = success ? data.msg : 'Run failed';
                const variant = success ? 'success' : 'error';
                addNotification(message, variant);
                if (success) {
                    createActivity({ time: dayjs().format(), type: 'manual_playbook_run' }).catch(() => {
                        console.error('Failed to create activity');
                    });
                }
                queryClient.invalidateQueries(queryKeys.playbooks);
                queryClient.invalidateQueries(queryKeys.playbooksV2);
                queryClient.invalidateQueries(queryKeys.teamPlaybooks);
                playbooks?.forEach((playbook) => {
                    queryClient.invalidateQueries(`${ELIGIBLE_ROWS_QUERY_KEY}-${playbook.uuid}`);
                });
            },
            onError: () => {
                addNotification('Run failed', 'error');
            },
        }
    );
};

export const useEligibleRows = (id?: string) => {
    const accessToken = Auth.accessToken;

    return useQuery<IEligibleRowsResponse>(
        `${ELIGIBLE_ROWS_QUERY_KEY}-${id}`,
        // @ts-expect-error TODO: refactor data fetching
        async () => playbooksAPI.getNumEligibleRows(accessToken, { uuid: id }),
        {
            enabled: !!accessToken && id != null,
        }
    );
};

interface PlaybookActivationPayload {
    id: string;
    isEnabled: boolean;
}

export const useTogglePlaybookActivation = () => {
    const queryClient = useQueryClient();
    const accessToken = Auth.accessToken;
    const { addNotification } = useNotifications();

    return useMutation<IStandardResponse, unknown, PlaybookActivationPayload>(
        async ({id, isEnabled}) => {
            // @ts-expect-error TODO: refactor data fetching
            return playbooksAPI.update(accessToken, id, { is_enabled: isEnabled });
        }, {
            onSuccess: (_data, payload) => {
                const message = payload.isEnabled ? 'Playbook enabled' : 'Playbook disabled';
                addNotification(message, 'success');
                queryClient.invalidateQueries(queryKeys.playbooks);
                queryClient.invalidateQueries(queryKeys.playbooksV2);
                queryClient.invalidateQueries(queryKeys.teamPlaybooks);
            }
        }
    );
};

export * from './use-create-playbook';
export * from './use-playbooks';
export * from './use-playbook-variables';
export * from './use-update-playbook';
export * from './use-update-background-playbooks-order';

export * from './types';
